<template>
    <div class="border rounded" v-if="image">
        <div class="bg-gray-300 p-2">
            <button-white @click="type = 'rectangle'" class="mr-4" :class="{'border-blue-500': type === 'rectangle'}">
                <i v-if="type === 'rectangle'" class="fa fa-square-o fa-lg"></i>
                <i v-else class="fa fa-square-o" style="color: grey"></i>
            </button-white>
            <button-white @click="type = 'circle'" class="mr-4" :class="{'border-blue-500': type === 'circle'}">
                <i v-if="type === 'circle'" class="fa fa-circle-o fa-lg"></i>
                <i v-if="type !== 'circle'" class="fa fa-circle-o" style="color: grey"></i>
            </button-white>
            <button-white @click="detection = !detection" class="mr-4" :class="{'border-blue-500': detection}">
                <i v-if="!detection" class="fa fa-magic" style="color: grey"></i>
                <i v-else class="fa fa-magic fa-lg"></i>
            </button-white>
            <button-white v-show="type === 'rectangle'" @click="rotateLeft" class="mr-4" :class="{'border-blue-500': type === 'rectangle'}">
                <i class="fa fa-undo"></i>
            </button-white>
            <button-white v-show="type === 'rectangle'" @click="rotateRight" :class="{'border-blue-500': type === 'rectangle'}">
                <i class="fa fa-repeat" ></i>
            </button-white>

            <div v-show="type === 'rectangle'">
                <input-text v-model="width">
                    Breite
                </input-text>

                <input-text v-model="height">
                    Höhe
                </input-text>
            </div>

            <div v-show="type === 'circle'">
                <input-text v-model="radius">
                    Radius
                </input-text>
            </div>

        </div>

        <div id="map" style="width:100%;height:400px;" class="p-2"></div>
    </div>
</template>

<script>
import L from "leaflet";
import Card from "./UI/Card";
import ButtonPrimary from "./UI/ButtonPrimary";
import SelectMenu from "./UI/SelectMenu";
import InputText from "./UI/InputText";
import ButtonWhite from "./UI/ButtonWhite";
import { floodFillOBB, looksLikeCircle, rotatePoint } from "../utils/geometry";

export default {
    name: "NavigationEdit",

    components: {
        ButtonWhite,
        InputText,
        SelectMenu,
        ButtonPrimary,
        Card,
    },

    props: ['value', 'floor'],

    data: () => ({
        height: 10,
        width: 10,
        radius: 10,
        angle: 0,
        type:  'rectangle',
        center: [0, 0],
        selectedId: null,
        map: null,
        rectangle: null,
        canvas: null,
        data: [],
        detection: true,
        disableClick: false,
        desks: [],
        defaultPosition: null,
    }),

    watch: {
        height() {
            this.updatePosition(this.center);
        },
        width() {
            this.updatePosition(this.center);
        },
        type() {
            this.updatePosition(this.center);
        },
        radius() {
            this.updatePosition(this.center);
        },

        async floor(curr,prev){
            if(prev && curr.id !== prev.id){
                //reload desks when the floor changes
                this.desks = [];
                await this.loadDesks();
            }

            //whenever the floor changes back to the current floor, put the desk to its default location
            if(this.item.floor_id === curr.id){
                this.item.position=this.defaultPosition;
            }else {
                //whenever the room changes set the position to null so that it can be selected explicitly by the user for the new room.
                this.item.position = null;
            }
            this.loadMap();

        }
    },

    computed: {
        item: {
            get() {
                return this.value;
            },
            set(value) {
                return this.$emit('input', value);
            },
        },
        image() {
            return this.floor.map_link;
        },
    },

    async mounted() {
        await this.loadDesks();
        this.defaultPosition = this.item?.position;

        //skip in case create desk
        if(this.item) {
            this.init();
        }
        this.loadMap();

    },

    methods: {
        loadDesks(){
            return new Promise((resolve)=>{
                window.axios.get('/admin/desks/getByFloor/'+this.floor.id).then(response=>{
                    this.desks = response.data.data.filter(d=> (this.item && d.id!==this.item.id && d.position && d.position.center[0]) || (!this.item && d.position && d.position.center[0]));
                    return resolve();
                })
            })
        },
        detectDeskItem(position) {
            if(this.disableClick){
                return;
            }
            if (!this.detection) {
                return this.updatePosition(position);
            }

            const start = { x: position[1], y: this.canvas.height - position[0] };
            const pixels = { data: this.data, width: this.canvas.width, height: this.canvas.height };
            const obb = floodFillOBB(start, pixels);

            if (!obb) {
                return this.updatePosition(position);
            }

            // Reject highlights that are far bigger than a plausible desk.
            if (Math.max(obb.halfWidth, obb.halfHeight) > 200) {
                this.init();
                return;
            }

            this.width = obb.halfWidth;
            this.height = obb.halfHeight;

            if (looksLikeCircle(obb)) {
                this.type = 'circle';
                this.radius = Math.round((obb.halfWidth + obb.halfHeight) / 2);
                this.angle = 0;
            } else {
                this.type = 'rectangle';
                this.radius = Math.max(this.width, this.height);
                this.angle = obb.degenerate ? 0 : obb.angleDegrees;
            }

            const newPosition = [
                Math.round(this.canvas.height - obb.center.y),
                Math.round(obb.center.x),
            ];
            this.updatePosition(newPosition);
        },
        init(){
            this.height = this.value && this.value.position ? this.value.position.height : 10;
            this.width = this.value && this.value.position ? this.value.position.width : 10;
            this.radius= this.value && this.value.position ? this.value.position.radius : 10;
            this.angle = this.value && this.value.position ? (this.value.position.angle || 0) : 0;
            this.type = this.value && this.value.position ? this.value.position.type : 'rectangle';
            this.center=  this.value && this.value.position ? this.value.position.center : [0, 0];
        },
        loadMap() {
            //refresh map everytime
            if (this.map != null) {
                this.map.remove();
                this.map = null;
            }
            const img = new Image();

            img.onload = () => {
                let minZoom = - Math.ceil( Math.max(img.width + img.height) / 1000 );
                this.map = L.map('map', {
                    crs: L.CRS.Simple,
                    minZoom: minZoom,
                    maxZoom: 2,
                    zoomSnap: 0.1,
                });
                this.createMap(img.width, img.height)
                this.loadCanvas(img);

                this.desks.forEach(desk=>{
                    this.addNonClickableRectangle(desk);
                })
            }

            img.src = this.image;
            img.crossOrigin = "Anonymous";
        },
        loadCanvas(img) {
            this.canvas = document.createElement('canvas');
            this.canvas.width = img.width;
            this.canvas.height = img.height;
            this.canvas
                .getContext('2d')
                .drawImage(img, 0, 0, img.width, img.height);

            let context = this.canvas.getContext('2d');
            let imageData = context.getImageData(0, 0, this.canvas.width, this.canvas.height);
            this.data = imageData.data;
        },
        createMap(width, height) {
            let bounds = [[0, 0], [height, width]];
            L.imageOverlay(this.image, bounds).addTo(this.map);
            this.map.setMaxBounds(bounds);
            this.map.fitBounds(bounds);

            this.map.on('click', (e) => {
                const p = e.latlng;
                //this.updatePosition([Math.floor(p.lat), Math.floor(p.lng)]);
                this.detectDeskItem([Math.floor(p.lat), Math.floor(p.lng)]);
            })

            //show the current desk only when its floor is selected.
            if(this.item && this.item.floor_id === this.floor.id)
            {
                this.add()
            }
        },
        updatePosition(pos) {
            //if highlighted/selected area is far bigger than a possible desk then dont highlight/select the region
            if(this.radius >200){
                //reset the parameters back to that of last selected desk
                this.init();
                return;
            }
            this.center = pos;
            let position = {
                center: pos,
                height: parseInt(this.height),
                width: parseInt(this.width),
                radius: parseInt(this.radius),
                type: this.type,
                angle: Math.round(this.angle)
            };

            let tmp = this.item || {};
            tmp.position = position;
            this.item = tmp;
            setTimeout(()=>{
                this.add();
            },100)
        },
        add() {
            if (this.rectangle) {
                this.rectangle.remove();
            }

            this.addRectangle(this.item);
        },
        rotateRectangle(item, rectangle){
            if(!rectangle || !item.position){
                return;
            }
            let center = rectangle.getBounds().getCenter();
            let rotationRadians = (item.position.angle * Math.PI) / 180;
            let rotatedCorners = rectangle.getLatLngs()[0].map((corner) => {
                const rotated = rotatePoint(corner, center, rotationRadians);
                return L.latLng(rotated.lat, rotated.lng);
            });

            rectangle.setLatLngs([rotatedCorners]);
        },
        addRectangle(item) {
            //mark current Desk
            if (!item || !item.position) {
                return;
            }

            if (item.position.type === 'circle') {
                this.rectangle = L.circle(item.position.center, {color: "green", radius: item.position.radius, weight: 1})
                    .addTo(this.map);
                return;
            }
            this.rectangle = L.rectangle([
                [item.position.center[0] + item.position.height, item.position.center[1] - item.position.width],
                [item.position.center[0] - item.position.height, item.position.center[1] + item.position.width],
            ], {color: "green", weight: 1})
                .addTo(this.map);

            this.rotateRectangle(item, this.rectangle);
        },
        addNonClickableRectangle(item) {
            if (!item.position) {
                return;
            }

            if (item.position.type === 'circle') {
                L.circle(item.position.center, {color: "blue", radius: item.position.radius, weight: 1})
                    .addTo(this.map);

                return;
            }
            let rectangle = L.rectangle([
                [item.position.center[0] + item.position.height, item.position.center[1] - item.position.width],
                [item.position.center[0] - item.position.height, item.position.center[1] + item.position.width],
            ], {color: "blue", weight: 1})
                .addTo(this.map).on('mouseover',()=>{
                    this.disableClick = true;
                }).on('mouseout',()=>{
                    this.disableClick = false;
                });

            this.rotateRectangle(item, rectangle);
        },
        rotateLeft(){
            this.angle += 10;
            this.updatePosition(this.center);
        },
        rotateRight(){
            this.angle -= 10;
            this.updatePosition(this.center);
        }
    }
}
</script>

<style scoped>

</style>
